import { ref, set, onValue } from 'firebase/database';
import { doc, updateDoc } from 'firebase/firestore';
import { database, firestore } from '../../../firebaseConfig';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../../../utils/cloudFunctions';
import { selectInstructionWithRotation, selectRandomWithRotation, greetingToneFromVariant, getEffectivePacing, hasInstructionBlocks, stripInstructionBlocks, findTopicInUserTopics } from '../emailTrackingUtils';
import { minimalSignalsSnapshot } from '../../leadSignalEngine';
import { getFirstDmName } from '../../../shared/enums';
import { logStatusChange } from '../../../shared/contactActivityLog';
import { resolveFollowUpSubject, SUBJECT_REPLY_LINE_ID } from '../../../utils/followUpSubject';
import { fetchPreviousSentEmail, isFollowUpTopicType } from '../previousEmailContext';
// Voice-Clone bridge — used when a Voice-Clone-mode template is selected.
// Standard-mode templates ignore this entirely.
import {
  isVoiceCloneTopicData,
  buildVoiceClonePayload,
  shouldGenerateSmartSubject,
  buildVoiceCloneSmartSubjectId,
  loadFreshContactRow,
} from '../../CloneTemplate/buildVoiceClonePayload';
import { readVoiceCloneError, buildVoiceCloneError } from '../../CloneTemplate/voiceCloneErrors';
import { emailGenerationCost } from '../../../config/creditPlans';
import { chargeCredits } from '../../../utils/creditCharge';
import { accountTextSignature } from '../../accountTextSignature';
import { formatReviewAuditContext } from '../../../shared/reviewAuditContext';
import { formatConnectorContext } from '../../../shared/connectorContext';

// =============================================================================
// findTopicByName — Bulk-mode helper that returns the raw { topicId, topicData }
// for a selected topic, searching the same scopes as the standard pipeline.
// Used by the Voice-Clone branch which needs the original topicData (mode,
// cloneConfig, etc.) before any standard-mode field extraction.
// =============================================================================
const findTopicByName = (selectedTopic, topicTypeMapping, userTopics) => {
  if (!selectedTopic || !userTopics) return null;
  const topicType = topicTypeMapping?.[selectedTopic];
  const scopes = [
    Object.entries(userTopics).filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v) && v.topic),
    userTopics.selectedTopics ? Object.entries(userTopics.selectedTopics) : [],
    userTopics.customTopics ? Object.entries(userTopics.customTopics) : [],
  ];
  // Pass 1: exact name match
  for (const entries of scopes) {
    for (const [topicId, topicData] of entries) {
      if (topicData?.topic === selectedTopic) return { topicId, topicData };
    }
  }
  // Pass 2: type match
  for (const entries of scopes) {
    for (const [topicId, topicData] of entries) {
      if (topicData?.type === selectedTopic || (topicType && topicData?.type === topicType)) {
        return { topicId, topicData };
      }
    }
  }
  return null;
};

// =============================================================================
// callVoiceCloneApi — Bulk-mode counterpart to callAiApi for Voice-Clone-mode
// templates. Deducts credits based on the template's quality tier and routes
// through the worldEmailCreator endpoint with `mode: 'voiceClone'`.
// Return shape mirrors callAiApi so generateEmailForContact stays uniform.
// =============================================================================
const callVoiceCloneApi = async ({
  contact,
  topicData,
  topicId,
  userId,
  userSettings,
  recipientInfo,
  customFieldsData,
  skipCreditDeduction,
  cloneIdOverride,
  textSignature,
}) => {
  const cloneConfig = topicData?.cloneConfig || {};
  // One-off CopyTwin switch from the Re-Generate menu — overrides the
  // template's saved clone for this single send only.
  const cloneId = cloneIdOverride || cloneConfig.cloneId;
  if (!cloneId) {
    throw new Error('CopyTwin template has no twin selected. Open the template editor and pick one.');
  }

  // Credit deduction — priced centrally so every generation path (manual, bulk,
  // outreach jobs, flow automation) charges the same. Legacy 4-tier ids
  // (basic / fine) are resolved inside emailGenerationCost().
  const tierId = cloneConfig.qualityTier || 'best';
  const creditCost = emailGenerationCost({ voiceCloneTier: tierId });

  if (userId && !skipCreditDeduction) {
    try {
      await chargeCredits({
        action: 'voice_clone_send',
        amount: creditCost,
        worldId: userId,
        metadata: { contactId: contact.id, templateId: topicId, cloneId, qualityTier: tierId },
      });
    } catch (creditErr) {
      if (creditErr.code === 'insufficient_credits') {
        throw new Error(`Not enough credits for CopyTwin email (${creditCost} credits required).`);
      }
      // Retries are exhausted — a CopyTwin mail is 12.5–22 credits, so it must
      // not be generated on the assumption that the charge landed.
      throw new Error(
        `Could not reserve ${creditCost} credits for the CopyTwin email. Nothing was generated — please retry.`
      );
    }
  }

  // Re-read the contact so research run during/just before the bulk run reaches
  // the generator even if the in-memory row is stale. Fail-safe on error.
  const freshContact = await loadFreshContactRow(userId, contact);

  const vcPayload = buildVoiceClonePayload({
    topicData,
    templateId: topicId,
    rowData: freshContact,
    userSettings,
    // Active workspace userId so the backend resolves the right RTDB path
    // when bulk-sending from inside a switched team workspace.
    userId,
    recipient: recipientInfo,
    firstName: getFirstDmName(freshContact),
    customFieldsData,
    includeSubject: shouldGenerateSmartSubject(topicData),
    cloneIdOverride: cloneIdOverride || undefined,
    textSignature,
  });
  if (!vcPayload) throw new Error('Could not build Voice-Clone payload.');

  const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(vcPayload),
  });
  if (!response.ok) {
    // Carry the structured error (code/details) so the bulk runner can show a
    // clear, actionable per-contact message — especially NO_ACTIVE_VARIANT,
    // which would otherwise fail every contact in the run with a cryptic code.
    const parsed = await readVoiceCloneError(response);
    throw buildVoiceCloneError(parsed);
  }
  const data = await response.json();

  // Subject fallback chain: backend smart subject → first static subject from
  // template → topic name. Mirrors how Standard mode resolves it.
  let finalSubject = data.subjectLine || '';
  let finalSubjectLineId = data.subjectLine
    ? buildVoiceCloneSmartSubjectId(data.variantId)
    : null;
  if (!finalSubject) {
    const staticLines = Array.isArray(topicData.subjectLines) ? topicData.subjectLines : [];
    if (staticLines.length > 0) {
      const pick = staticLines[Math.floor(Math.random() * staticLines.length)];
      const text = typeof pick === 'object' ? pick.text : pick;
      finalSubject = (text || '')
        .replace(/{{business_name}}/g, contact.business_name || '')
        .replace(/{{name}}/g, getFirstDmName(contact) || '')
        .replace(/{{company}}/g, '');
      finalSubjectLineId = typeof pick === 'object' && pick.id ? pick.id : null;
    } else {
      finalSubject = topicData.topic || `Hi ${getFirstDmName(contact) || ''}`.trim();
    }
  }

  return {
    subject: finalSubject,
    body: data.emailContent || '',
    subjectLineId: finalSubjectLineId,
    variantId: data.variantId || null,
  };
};

// E-Mail für einen bestimmten Kontakt generieren
// 🔥 FIXED: Gibt jetzt auch instructionId, subjectLineId und templateId zurück für korrektes Tracking
export const generateEmailForContact = async (
  contact,
  selectedTopic,
  contextInfo,
  userId,
  userSettings,
  aiSettings,
  userTopics,
  topicTypeMapping,
  getDefaultTopicNote,
  customContextFields,
  recipientInfo,
  frameworkPrompt,
  { skipCreditDeduction = false, excludeInstructionId = null, customInstructions = null, cloneIdOverride = null, textSignature = '' } = {}
) => {
  try {
    const isDecisionMakerRecipient = !!(recipientInfo?.name);

    // ===== VOICE-CLONE BRANCH =====
    // Skip the entire standard prompt-assembly + callAiApi pipeline and route
    // through worldEmailCreator with `mode: 'voiceClone'` instead. The
    // backend loads the clone + template fresh from RTDB.
    const found = findTopicByName(selectedTopic, topicTypeMapping, userTopics);
    if (found && isVoiceCloneTopicData(found.topicData)) {
      const customFieldsData = (customContextFields || []).reduce((acc, cf) => {
        const val = contact[cf.key];
        if (val != null && val !== '') acc.push({ key: cf.key, label: cf.label, value: String(val) });
        return acc;
      }, []);

      const vc = await callVoiceCloneApi({
        contact,
        topicData: found.topicData,
        topicId: found.topicId,
        userId,
        userSettings,
        recipientInfo,
        customFieldsData,
        skipCreditDeduction,
        cloneIdOverride,
        textSignature,
      });

      console.log('🎯 [VOICE-CLONE-BULK] Generated for contact', contact?.id, {
        templateId: found.topicId,
        variantId: vc.variantId,
      });

      return {
        subject: vc.subject || `Zusammenarbeit mit ${contact.business_name || 'Ihrem Unternehmen'}`,
        body: vc.body || '',
        prompt: '', // not used in Voice-Clone mode
        _trackingInstructionId: null,
        _trackingSubjectLineId: vc.subjectLineId,
        _trackingTemplateId: found.topicId,
        _trackingVariantId: vc.variantId,
      };
    }
    // ===== END VOICE-CLONE BRANCH — standard pipeline below is unchanged =====

    // 🔥 FIXED: generatePromptForContact gibt jetzt ein Objekt zurück
    const promptResult = generatePromptForContact(
      contact, 
      selectedTopic, 
      contextInfo, 
      userSettings, 
      aiSettings, 
      userTopics, 
      topicTypeMapping,
      getDefaultTopicNote,
      customContextFields,
      { isDecisionMakerRecipient, excludeInstructionId }
    );
    
    // Dekonstruiere das Ergebnis
    const { prompt: promptForContact, topicSpecificNote: resolvedNote, instructionId, selectedPacing: selectedVariantPacing, templateId, generalInfo: topicGeneralInfo, aiAdherence: topicAiAdherence, formattingStyle: topicFormattingStyle, model: topicModel, maxTokens: topicMaxTokens, writingLevel: topicWritingLevel, aiTemperature: topicAiTemperature, hyperIntelligence: topicHyperIntelligence, personalizationGuide: topicPersonalizationGuide, referenceFile: topicReferenceFile, ultraMode: topicUltraMode, smartGreeting: topicSmartGreeting, greetingCasualPrefixes: topicGreetingCasualPrefixes, greetingFormalPrefixes: topicGreetingFormalPrefixes, smartSubjectLine: topicSmartSubjectLine, smartSubjectStyle: topicSmartSubjectStyle, smartSubjectInstructions: topicSmartSubjectInstructions, language: topicLanguage, emailPreference: topicEmailPref } = promptResult;
    
    const customFieldsData = (customContextFields || []).reduce((acc, cf) => {
      const val = contact[cf.key];
      if (val != null && val !== '') {
        acc.push({ key: cf.key, label: cf.label, value: String(val) });
      }
      return acc;
    }, []);

    const apiResponse = await callAiApi(
      promptForContact,
      contact,
      selectedTopic,
      userId,
      userSettings,
      aiSettings,
      topicTypeMapping,
      userTopics,
      getDefaultTopicNote,
      topicGeneralInfo,
      topicAiAdherence,
      topicFormattingStyle,
      templateId,
      topicModel,
      topicMaxTokens,
      topicWritingLevel,
      topicAiTemperature,
      topicHyperIntelligence,
      topicPersonalizationGuide,
      topicReferenceFile,
      topicUltraMode,
      topicSmartGreeting,
      topicGreetingCasualPrefixes,
      topicGreetingFormalPrefixes,
      resolvedNote,
      recipientInfo,
      topicLanguage,
      customFieldsData,
      skipCreditDeduction,
      topicSmartSubjectLine,
      topicSmartSubjectStyle,
      topicSmartSubjectInstructions,
      frameworkPrompt,
      topicEmailPref,
      customInstructions,
      selectedVariantPacing,
      textSignature
    );
    
    console.log('📧 [GENERATE-EMAIL] ✅ Email generated with tracking IDs:', {
      instructionId,
      subjectLineId: apiResponse.subjectLineId,
      templateId
    });
    
    // 🔥 FIXED: Gib alle Tracking-IDs zurück
    return {
      subject: apiResponse.subject || `Zusammenarbeit mit ${contact.business_name || 'Ihrem Unternehmen'}`,
      body: apiResponse.body || '',
      prompt: promptForContact,
      // 🔥 NEU: Tracking-IDs für korrektes Stats-Tracking
      _trackingInstructionId: instructionId,
      _trackingSubjectLineId: apiResponse.subjectLineId,
      _trackingTemplateId: templateId
    };
  } catch (error) {
    console.error(`❌ Fehler bei der E-Mail-Generierung für Kontakt:`, error);
    throw new Error(`E-Mail-Generierung fehlgeschlagen für ${contact.business_name || 'Kontakt'}: ${error.message}`);
  }
};

// Prompt für einen Kontakt generieren
// 🔥 FIXED: Gibt jetzt ein Objekt zurück mit { prompt, instructionId, templateId } für korrektes Tracking
const generatePromptForContact = (
  contact, 
  selectedTopic, 
  contextInfo, 
  userSettings, 
  aiSettings,
  userTopics,
  topicTypeMapping,
  getDefaultTopicNote,
  customContextFields,
  { isDecisionMakerRecipient = false, excludeInstructionId = null } = {}
) => {
  let topicSpecificNote = "";
  let instructionId = null;
  let selectedPacing = null;
  let templateId = null;
  let generalInfo = '';
  let aiAdherence = 'exact';
  let formattingStyle = 'as-written';
  let templateModel = null;
  let templateMaxTokens = null;
  let templateWritingLevel = 'middle_school';
  let templateAiTemperature = null;
  let hyperIntelligence = false;
  let personalizationGuide = '';
  let referenceFile = null;
  let ultraMode = false;
  let smartGreeting = false;
  let greetingCasualPrefixes = ['Hey', 'Hi', 'Hello'];
  let greetingFormalPrefixes = ['Dear'];
  let smartSubjectLine = false;
  let smartSubjectStyle = 'neutral';
  let smartSubjectInstructions = '';
  let templateLanguage = null;
  let templateEmailPreference = 'auto';
  
  console.log('📧 [BULK-PROMPT] Generating prompt for contact:', contact.business_name);
  console.log('📧 [BULK-PROMPT] selectedTopic:', selectedTopic);
  console.log('📧 [BULK-PROMPT] contact has tags:', !!contact.tags);
  
  // Prüfe, ob das selectedTopic von einem AI Tag stammt
  let isAiTagTopic = false;
  let matchedTopicFromAiTag = null;
  
  if (contact.tags && typeof contact.tags === 'object') {
    const tagsArray = Object.entries(contact.tags).map(([tagId, tagData]) => ({
      id: tagId,
      ...tagData
    }));
    
    // Sortiere nach appliedAt Datum (neueste zuerst)
    tagsArray.sort((a, b) => {
      const dateA = new Date(a.appliedAt || 0);
      const dateB = new Date(b.appliedAt || 0);
      return dateB - dateA;
    });
    
    if (tagsArray.length > 0) {
      const newestTag = tagsArray[0];
      
      // 🔥 WICHTIG: Immer die neueste Version aus userTopics laden (nicht vom Kontakt)
      // 🔥 LÖSUNG: IGNORIERE appliedTopic - verwende NUR AI Tag ID!
      if (newestTag.id) {
        const matchingTopic = Object.entries(userTopics).find(([topicId, topicData]) => {
          return topicData.aiTagId === newestTag.id;
        });
        
        if (matchingTopic && matchingTopic[1].topic === selectedTopic) {
          const [topicId, topicData] = matchingTopic;
          matchedTopicFromAiTag = { topicId, topicData };
          isAiTagTopic = true;
          templateId = topicId;
          generalInfo = topicData.generalInfo || '';
          aiAdherence = topicData.aiAdherence || 'exact';
          formattingStyle = topicData.formattingStyle || 'as-written';
          templateModel = topicData.model || null;
          templateWritingLevel = topicData.writingLevel || 'middle_school';
          templateAiTemperature = topicData.aiTemperature != null ? topicData.aiTemperature : null;
          hyperIntelligence = topicData.hyperIntelligence || false;
          personalizationGuide = topicData.personalizationGuide || '';
          referenceFile = topicData.referenceFile || null;
          ultraMode = topicData.ultraMode || false;
          smartGreeting = topicData.smartGreeting || false;
          greetingCasualPrefixes = Array.isArray(topicData.greetingCasualPrefixes) ? topicData.greetingCasualPrefixes : (Array.isArray(topicData.greetingPrefixes) ? topicData.greetingPrefixes : ['Hey', 'Hi', 'Hello']);
          greetingFormalPrefixes = Array.isArray(topicData.greetingFormalPrefixes) ? topicData.greetingFormalPrefixes : ['Dear'];
          smartSubjectLine = topicData.smartSubjectLine || false;
          smartSubjectStyle = topicData.smartSubjectStyle || 'neutral';
          smartSubjectInstructions = topicData.smartSubjectInstructions || '';
          templateMaxTokens = topicData.maxTokens || null;
          templateLanguage = topicData.language || null;
          templateEmailPreference = topicData.emailPreference || 'auto';
          
          if (topicData.topicSpecificNotes && Array.isArray(topicData.topicSpecificNotes) && topicData.topicSpecificNotes.length > 0) {
            // Rotation / shuffle-bag (keyed per template) so all variants are
            // used once before any repeats. lead_pacing no longer used; greeting
            // tone is driven by the selected variant's own pacing. excludeInstructionId
            // is an extra exclude (e.g. on regenerate) so the same variant isn't repeated.
            const randomInstruction = selectInstructionWithRotation(topicData.topicSpecificNotes, null, topicId, excludeInstructionId);
            console.log('🎲 [HELPER-ROTATION] Selected instruction:', randomInstruction, '(rotationKey:', topicId, ')');
            topicSpecificNote = typeof randomInstruction === 'object' ? randomInstruction.text : randomInstruction;
            instructionId = typeof randomInstruction === 'object' && randomInstruction.id ? randomInstruction.id : `legacy_${topicId}`;
            selectedPacing = typeof randomInstruction === 'object' ? (randomInstruction.pacing || null) : null;
          } else if (topicData.topicSpecificNote) {
            console.log('🎲 [HELPER-RANDOM] Single instruction found:', topicData.topicSpecificNote);
            topicSpecificNote = topicData.topicSpecificNote;
            instructionId = `legacy_${topicId}`;
          }
        }
      }
      
      // Fallback: Prüfe alte gespeicherte Version
      if (!isAiTagTopic && newestTag.selectedTopic && newestTag.selectedTopic === selectedTopic) {
        isAiTagTopic = true;
        console.log('📧 [BULK-PROMPT] Topic comes from stored AI Tag (legacy), searching in userTopics...');
      }
    }
  }
  
  // Wenn das Topic von einem AI Tag stammt aber noch keine Instruction gefunden, suche in userTopics
  if (isAiTagTopic && !topicSpecificNote) {
    // Suche nach dem Topic in userTopics (sowohl selectedTopics als auch customTopics)
    const matchingTopicEntry = Object.entries(userTopics).find(([topicId, topicData]) => {
      return topicData.topic === selectedTopic || topicData.type === selectedTopic;
    });
    
    if (matchingTopicEntry) {
      const [topicId, matchingTopic] = matchingTopicEntry;
      templateId = topicId;
      generalInfo = matchingTopic.generalInfo || '';
      aiAdherence = matchingTopic.aiAdherence || 'exact';
      formattingStyle = matchingTopic.formattingStyle || 'as-written';
      templateModel = matchingTopic.model || null;
      templateWritingLevel = matchingTopic.writingLevel || 'middle_school';
      templateAiTemperature = matchingTopic.aiTemperature != null ? matchingTopic.aiTemperature : null;
      hyperIntelligence = matchingTopic.hyperIntelligence || false;
      personalizationGuide = matchingTopic.personalizationGuide || '';
      referenceFile = matchingTopic.referenceFile || null;
      ultraMode = matchingTopic.ultraMode || false;
      smartGreeting = matchingTopic.smartGreeting || false;
      greetingCasualPrefixes = Array.isArray(matchingTopic.greetingCasualPrefixes) ? matchingTopic.greetingCasualPrefixes : (Array.isArray(matchingTopic.greetingPrefixes) ? matchingTopic.greetingPrefixes : ['Hey', 'Hi', 'Hello']);
      greetingFormalPrefixes = Array.isArray(matchingTopic.greetingFormalPrefixes) ? matchingTopic.greetingFormalPrefixes : ['Dear'];
      smartSubjectLine = matchingTopic.smartSubjectLine || false;
      smartSubjectStyle = matchingTopic.smartSubjectStyle || 'neutral';
      smartSubjectInstructions = matchingTopic.smartSubjectInstructions || '';
      templateMaxTokens = matchingTopic.maxTokens || null;
      templateLanguage = matchingTopic.language || null;
      templateEmailPreference = matchingTopic.emailPreference || 'auto';
      console.log('📧 [BULK-PROMPT] Found matching topic in userTopics:', matchingTopic);
      
      if (matchingTopic.topicSpecificNotes && Array.isArray(matchingTopic.topicSpecificNotes) && matchingTopic.topicSpecificNotes.length > 0) {
        // Rotation / shuffle-bag (keyed per template) so all variants are used
        // once before any repeats. lead_pacing no longer used; greeting tone is
        // driven by the selected variant's own pacing. excludeInstructionId is an
        // extra exclude (e.g. on regenerate) so the same variant isn't repeated.
        const randomInstruction = selectInstructionWithRotation(matchingTopic.topicSpecificNotes, null, topicId, excludeInstructionId);
        console.log('🎲 [HELPER-ROTATION] Selected instruction:', randomInstruction, '(rotationKey:', topicId, ')');
        topicSpecificNote = typeof randomInstruction === 'object' ? randomInstruction.text : randomInstruction;
        instructionId = typeof randomInstruction === 'object' && randomInstruction.id ? randomInstruction.id : `legacy_${topicId}`;
        selectedPacing = typeof randomInstruction === 'object' ? (randomInstruction.pacing || null) : null;
      } else if (matchingTopic.topicSpecificNote) {
        console.log('🎲 [HELPER-RANDOM] Single instruction found:', matchingTopic.topicSpecificNote);
        topicSpecificNote = matchingTopic.topicSpecificNote;
        instructionId = `legacy_${topicId}`;
      } else {
        console.log('📧 [BULK-PROMPT] ⚠️ No topicSpecificNote in matching topic');
      }
    } else {
      console.log('📧 [BULK-PROMPT] ⚠️ No matching topic found in userTopics for:', selectedTopic);
    }
  }
  
  // Fallback auf die normale Topic-Type Logik, wenn kein AI Tag Topic oder keine Note gefunden
  if (!topicSpecificNote) {
    console.log('📧 [BULK-PROMPT] Using standard topic type logic');
    
    const topicType = topicTypeMapping[selectedTopic];
    
    // Helper: apply all template settings from a found topic entry
    const applyFoundTopic = (topicId, foundTopic) => {
      templateId = topicId;
      generalInfo = foundTopic.generalInfo || '';
      aiAdherence = foundTopic.aiAdherence || 'exact';
      formattingStyle = foundTopic.formattingStyle || 'as-written';
      templateModel = foundTopic.model || null;
      templateWritingLevel = foundTopic.writingLevel || 'middle_school';
      templateAiTemperature = foundTopic.aiTemperature != null ? foundTopic.aiTemperature : null;
      hyperIntelligence = foundTopic.hyperIntelligence || false;
      personalizationGuide = foundTopic.personalizationGuide || '';
      referenceFile = foundTopic.referenceFile || null;
      ultraMode = foundTopic.ultraMode || false;
      smartGreeting = foundTopic.smartGreeting || false;
      greetingCasualPrefixes = Array.isArray(foundTopic.greetingCasualPrefixes) ? foundTopic.greetingCasualPrefixes : (Array.isArray(foundTopic.greetingPrefixes) ? foundTopic.greetingPrefixes : ['Hey', 'Hi', 'Hello']);
      greetingFormalPrefixes = Array.isArray(foundTopic.greetingFormalPrefixes) ? foundTopic.greetingFormalPrefixes : ['Dear'];
      smartSubjectLine = foundTopic.smartSubjectLine || false;
      smartSubjectStyle = foundTopic.smartSubjectStyle || 'neutral';
      smartSubjectInstructions = foundTopic.smartSubjectInstructions || '';
      templateMaxTokens = foundTopic.maxTokens || null;
      templateLanguage = foundTopic.language || null;
      templateEmailPreference = foundTopic.emailPreference || 'auto';
      
      if (foundTopic.topicSpecificNotes && Array.isArray(foundTopic.topicSpecificNotes) && foundTopic.topicSpecificNotes.length > 0) {
        // Rotation / shuffle-bag (keyed per template) so all variants are used
        // once before any repeats. lead_pacing no longer used; greeting tone is
        // driven by the selected variant's own pacing. excludeInstructionId is an
        // extra exclude (e.g. on regenerate) so the same variant isn't repeated.
        const randomInstruction = selectInstructionWithRotation(foundTopic.topicSpecificNotes, null, topicId, excludeInstructionId);
        console.log('🎲 [HELPER-ROTATION] Selected instruction in fallback:', randomInstruction, '(rotationKey:', topicId, ')');
        topicSpecificNote = typeof randomInstruction === 'object' ? randomInstruction.text : randomInstruction;
        instructionId = typeof randomInstruction === 'object' && randomInstruction.id ? randomInstruction.id : `legacy_${topicId}`;
        selectedPacing = typeof randomInstruction === 'object' ? (randomInstruction.pacing || null) : null;
      } else if (foundTopic.topicSpecificNote) {
        console.log('🎲 [HELPER-RANDOM] Single instruction found in fallback:', foundTopic.topicSpecificNote);
        topicSpecificNote = foundTopic.topicSpecificNote;
        instructionId = `legacy_${topicId}`;
      }
    };
    
    // Pass 1: exact name match (ensures assigned custom templates like "Stepstone" are found
    // before standard templates with the same type like "Introduce Yourself")
    const foundByName = Object.entries(userTopics).find(([topicId, topic]) => topic.topic === selectedTopic);
    if (foundByName) {
      console.log('📧 [BULK-PROMPT] Found topic by exact name:', selectedTopic, '(id:', foundByName[0], ')');
      applyFoundTopic(foundByName[0], foundByName[1]);
    }

    // Pass 2: type-based fallback (only if name search didn't find anything)
    if (!topicSpecificNote && topicType) {
      const foundTopicEntry = Object.entries(userTopics).find(([topicId, topic]) => topic.type === topicType);
      
      if (foundTopicEntry) {
        applyFoundTopic(foundTopicEntry[0], foundTopicEntry[1]);
        if (!topicSpecificNote) {
          topicSpecificNote = getDefaultTopicNote(topicType);
          console.log('📧 [BULK-PROMPT] Using default topic note:', topicSpecificNote);
        }
      } else {
        topicSpecificNote = getDefaultTopicNote(topicType);
        console.log('📧 [BULK-PROMPT] Using default topic note:', topicSpecificNote);
      }
    }
  }
  
  // Erstelle eine schlanke Zusammenfassung der Kontaktdaten für die KI
  const businessName = contact.business_name || 'Unbekanntes Unternehmen';
  const firstNameOrContact = getFirstDmName(contact) || '';
  const businessType = contact.lead_type || 'Unternehmer';
  const rawDescription = contact.lead_description || '';
  const businessDescription = contact.researchIntelligence?.instagramInsight
    ? rawDescription.replace(/\n?\[IG Research\].*$/s, '').trim()
    : rawDescription;
  const businessCity = contact.lead_city || '';
  const website = contact.lead_website || '';
  
  // Extract outreach notes from contact
  const outreachNotes = (contact.lead_notes || [])
    .filter(n => n.type === 'outreach')
    .map(n => n.text)
    .join('\n');

  const igInsight = contact.researchIntelligence?.instagramInsight;
  const igPosts = contact.researchIntelligence?.instagramPosts;
  const igLines = [];
  if (igInsight) {
    igLines.push(`[IG RESEARCH] Instagram (@${igInsight.handle}): ${igInsight.summary}`);
    if (igInsight.uniqueFacts?.length) igLines.push(`Key facts: ${igInsight.uniqueFacts.join('; ')}`);
    if (igInsight.topics?.length) igLines.push(`Topics: ${igInsight.topics.join(', ')}`);
    if (igInsight.targetAudience) igLines.push(`Their audience: ${igInsight.targetAudience}`);
    if (igInsight.painPoints?.length) igLines.push(`Pain points: ${igInsight.painPoints.join('; ')}`);
    if (igInsight.sellingPoints?.length) igLines.push(`Their USPs: ${igInsight.sellingPoints.join('; ')}`);
    if (igInsight.salesAngle) igLines.push(`Recommended angle: ${igInsight.salesAngle}`);
    if (igInsight.activityScore != null) igLines.push(`Activity: ${igInsight.activityScore}/10, Engagement: ${igInsight.engagementScore ?? '?'}/10${igInsight.engagementTrend && igInsight.engagementTrend !== 'stable' ? ` (${igInsight.engagementTrend})` : ''}`);
    if (igInsight.isDeadAccount) igLines.push(`WARNING: Dead account — no recent posts and zero engagement.`);
    else if (igInsight.isActive === false) igLines.push(`Note: Inactive on Instagram (${igInsight.daysSinceLastPost}d since last post)`);
    if (Array.isArray(igPosts) && igPosts.length > 0) {
      const recentPosts = igPosts
        .filter(p => p.text)
        .slice(0, 3)
        .map((p, i) => {
          const excerpt = p.text.length > 120 ? p.text.substring(0, 120) + '...' : p.text;
          const stats = [p.like_count ? `${p.like_count} likes` : '', p.comment_count ? `${p.comment_count} comments` : ''].filter(Boolean).join(', ');
          return `      ${i + 1}. "${excerpt}"${stats ? ` (${stats})` : ''}`;
        });
      if (recentPosts.length > 0) igLines.push(`Recent posts:\n${recentPosts.join('\n')}`);
    }
  }
  const igLine = igLines.length > 0 ? '\n    ' + igLines.join('\n    ') : '';

  const bulkWa = contact.websiteAnalysis;
  const waLines = [];
  if (bulkWa) {
    if (bulkWa.cms) waLines.push(`CMS: ${bulkWa.cms}${bulkWa.version_estimate ? ` ${bulkWa.version_estimate}` : ''}`);
    const bulkPc = bulkWa.product_count ?? bulkWa.productCount;
    if (bulkPc != null) waLines.push(`Product count: ~${bulkPc}`);
    const bulkAge = bulkWa.website_age || bulkWa.websiteAge;
    if (bulkAge) waLines.push(`Website: ${bulkAge.label || `score ${bulkAge.score}`} (${bulkAge.estimated_era || bulkAge.estimatedEra || ''})`);
    if (bulkWa.industry) waLines.push(`Industry: ${bulkWa.industry}`);
    if (bulkWa.recommended_sales_angle) waLines.push(`Sales angle: ${bulkWa.recommended_sales_angle}`);
  }
  const waLine = waLines.length > 0 ? '\n    [WEBSITE ANALYSIS] ' + waLines.join(' | ') : '';

  const bulkSeo = contact.researchIntelligence?.seoAnalysis;
  const seoLines = [];
  if (bulkSeo && bulkSeo.data) {
    const core = bulkSeo.data.core;
    const vis = core?.visibility?.value;
    const kwTotal = core?.keywordsTotal;
    const kwTop10 = core?.keywordsTop10;
    const head = [];
    if (vis != null) head.push(`Visibility ${Number(vis).toFixed(2)}`);
    if (kwTotal != null) head.push(`${kwTotal} keywords`);
    if (kwTop10 != null) head.push(`${kwTop10} in Top 10`);
    if (head.length) seoLines.push(head.join(' · '));
    const bulkTv = bulkSeo.data.trafficValue;
    if (bulkTv?.estimatedTrafficMonthly != null) seoLines.push(`~${bulkTv.estimatedTrafficMonthly.toLocaleString('de-DE')} visitors/mo${bulkTv.estimatedValueMonthly ? ` (~€${bulkTv.estimatedValueMonthly.toLocaleString('de-DE')}/mo)` : ''}`);
    const bulkVt = bulkSeo.data.visibilityTrend;
    if (bulkVt?.changePct != null) seoLines.push(`Visibility ${bulkVt.direction === 'up' ? 'GAINING' : bulkVt.direction === 'down' ? 'LOSING' : 'flat'} ${bulkVt.changePct > 0 ? '+' : ''}${bulkVt.changePct}% (${bulkVt.overWindow || '12M'})`);
    if (bulkSeo.data.backlinks?.total != null) seoLines.push(`Backlinks ${bulkSeo.data.backlinks.total}`);
    const bulkComps = Array.isArray(bulkSeo.data.competitors) ? bulkSeo.data.competitors.slice(0, 2) : [];
    if (bulkComps.length) seoLines.push(`Competitors: ${bulkComps.map(c => c.domain).join(', ')}`);
    const bulkOpps = Array.isArray(bulkSeo.data.opportunities) ? bulkSeo.data.opportunities.slice(0, 2) : [];
    if (bulkOpps.length) seoLines.push(`Quick wins: ${bulkOpps.map(o => `"${o.keyword}" (pos ${o.position})`).join(', ')}`);
  }
  const seoLine = seoLines.length > 0 ? '\n    [SEO ANALYSIS] ' + seoLines.join(' | ') : '';
  const reviewLine = formatReviewAuditContext(contact.researchIntelligence?.googleReviewAudit)
    .replace(/\n/g, '\n    ');
  const connectorLine = formatConnectorContext(contact.researchIntelligence).replace(/\n/g, '\n    ');

  const cfLines = (customContextFields || [])
    .map((cf) => {
      const val = contact[cf.key];
      return val ? `    ${cf.label}: ${val}` : '';
    })
    .filter(Boolean)
    .join('\n');

  const prompt = `
    BUSINESS INFORMATION:
    Name: ${businessName}
    Type: ${businessType}
    Description: ${businessDescription}
    City: ${businessCity}
    ${website ? `Website: ${website}` : ''}${cfLines ? `\n${cfLines}` : ''}${igLine}${waLine}${seoLine}${reviewLine}${connectorLine}
    
    CONTACT INFORMATION:
    ${firstNameOrContact ? `Name: ${firstNameOrContact}` : 'Name: not available (do NOT invent or guess a name)'}
    
    CONTEXT:
    ${contextInfo || ''}
    ${outreachNotes ? `\n    IMPORTANT OUTREACH NOTES (consider these carefully):\n    ${outreachNotes}` : ''}
  `;
  
  console.log('📧 [BULK-PROMPT] ✅ Generated prompt with tracking IDs:', { instructionId, templateId });
  
  // 🔥 FIXED: Gibt jetzt Objekt mit allen Tracking-Infos zurück
  return {
    prompt,
    topicSpecificNote,
    instructionId,
    selectedPacing,
    templateId,
    generalInfo,
    aiAdherence,
    formattingStyle,
    model: templateModel,
    maxTokens: templateMaxTokens,
    writingLevel: templateWritingLevel,
    aiTemperature: templateAiTemperature,
    hyperIntelligence,
    personalizationGuide,
    referenceFile,
    ultraMode,
    smartGreeting,
    greetingCasualPrefixes,
    greetingFormalPrefixes,
    smartSubjectLine,
    smartSubjectStyle,
    smartSubjectInstructions,
    language: templateLanguage,
    emailPreference: templateEmailPreference,
  };
};

// 🔥 NEU: Topic-spezifische Notiz für Bulk-Modus holen (gleiche Logik wie NormalMode)
const getTopicSpecificNoteForBulk = (selectedTopic, contact, userTopics, topicTypeMapping, getDefaultTopicNote, { isDecisionMakerRecipient = false } = {}) => {
  if (!selectedTopic) return "";
  
  console.log('📧 [BULK-TOPIC-NOTE] Getting topic-specific note for:', selectedTopic);
  console.log('📧 [BULK-TOPIC-NOTE] Contact has tags:', !!contact?.tags);
  
  // Wenn das Topic von einem AI Tag stammt, suche direkt in userTopics (wie im NormalMode)
  if (contact?.tags && typeof contact.tags === 'object') {
    const tagsArray = Object.entries(contact.tags).map(([tagId, tagData]) => ({
      id: tagId,
      ...tagData
    }));
    
    // Sortiere nach appliedAt Datum (neueste zuerst)
    tagsArray.sort((a, b) => {
      const dateA = new Date(a.appliedAt || 0);
      const dateB = new Date(b.appliedAt || 0);
      return dateB - dateA;
    });
    
    if (tagsArray.length > 0) {
      const newestTag = tagsArray[0];
      
      // Prüfe, ob das Topic vom neuesten Tag kommt
      if (newestTag.selectedTopic && newestTag.selectedTopic === selectedTopic) {
        console.log('📧 [BULK-TOPIC-NOTE] Topic matches AI Tag selectedTopic, searching userTopics...');
        
        // Suche nach dem Topic in userTopics (wie im Normal-Modus)
        const matchingEntry = Object.entries(userTopics).find(([, topicData]) => {
          return topicData.topic === selectedTopic || topicData.type === selectedTopic;
        });
        const matchingTopicId = matchingEntry ? matchingEntry[0] : null;
        const matchingTopic = matchingEntry ? matchingEntry[1] : null;
        
        if (matchingTopic) {
          if (matchingTopic.topicSpecificNotes && Array.isArray(matchingTopic.topicSpecificNotes) && matchingTopic.topicSpecificNotes.length > 0) {
            // Rotation / shuffle-bag keyed per template (same key as the main path).
            const randomInstruction = selectInstructionWithRotation(matchingTopic.topicSpecificNotes, null, matchingTopicId);
            console.log('🎲 [HELPER-NOTE-ROTATION] Selected instruction:', randomInstruction, '(rotationKey:', matchingTopicId, ')');
            return typeof randomInstruction === 'object' ? randomInstruction.text : randomInstruction;
          }
          // Backwards compatibility: Fallback auf einzelne Instruction
          else if (matchingTopic.topicSpecificNote) {
            console.log('🎲 [HELPER-NOTE-RANDOM] Single instruction found in AI Tag:', matchingTopic.topicSpecificNote);
            return matchingTopic.topicSpecificNote;
          }
        }
      }
    }
  }
  
  console.log('📧 [BULK-TOPIC-NOTE] Using standard logic');
  
  // Fallback: Standard-Logik (wie im NormalMode)
  // Name-first search: find by exact topic name first, then fall back to type
  const topicType = topicTypeMapping[selectedTopic];
  const matchingFallbackEntry = Object.entries(userTopics).find(([, topic]) => topic.topic === selectedTopic)
    || (topicType ? Object.entries(userTopics).find(([, topic]) => topic.type === topicType) : null);
  const matchingTopicId = matchingFallbackEntry ? matchingFallbackEntry[0] : null;
  const matchingTopic = matchingFallbackEntry ? matchingFallbackEntry[1] : null;
  
  if (!matchingTopic && !topicType) return "";
  
  if (matchingTopic) {
    if (matchingTopic.topicSpecificNotes && Array.isArray(matchingTopic.topicSpecificNotes) && matchingTopic.topicSpecificNotes.length > 0) {
      // Rotation / shuffle-bag keyed per template (same key as the main path).
      const randomInstruction = selectInstructionWithRotation(matchingTopic.topicSpecificNotes, null, matchingTopicId);
      console.log('🎲 [HELPER-NOTE-FALLBACK-ROTATION] Selected instruction in fallback:', randomInstruction, '(rotationKey:', matchingTopicId, ')');
      return typeof randomInstruction === 'object' ? randomInstruction.text : randomInstruction;
    }
    // Backwards compatibility: Fallback auf einzelne Instruction
    else if (matchingTopic.topicSpecificNote) {
      console.log('🎲 [HELPER-NOTE-FALLBACK-RANDOM] Single instruction found in fallback:', matchingTopic.topicSpecificNote);
      return matchingTopic.topicSpecificNote;
    }
  }
  
  // Fallback auf Standard-Anweisungen
  const defaultNote = getDefaultTopicNote(topicType);
  console.log('📧 [BULK-TOPIC-NOTE] ✅ Using default topic note:', defaultNote);
  return defaultNote;
};

// API für E-Mail-Generierung aufrufen
// 🔥 FIXED: Gibt jetzt auch subjectLineId zurück für korrektes Tracking
const callAiApi = async (
  prompt,
  contact,
  selectedTopic,
  userId,
  userSettings,
  aiSettings,
  topicTypeMapping,
  userTopics = {},
  getDefaultTopicNote,
  topicGeneralInfo = '',
  topicAiAdherence = 'exact',
  topicFormattingStyle = 'as-written',
  templateId = null,
  topicModel = null,
  topicMaxTokens = null,
  topicWritingLevel = 'middle_school',
  topicAiTemperature = null,
  topicHyperIntelligence = false,
  topicPersonalizationGuide = '',
  topicReferenceFile = null,
  topicUltraMode = false,
  topicSmartGreeting = false,
  topicGreetingCasualPrefixes = ['Hey', 'Hi', 'Hello'],
  topicGreetingFormalPrefixes = ['Dear'],
  resolvedTopicSpecificNote = null,
  recipientInfo = null,
  topicLanguage = null,
  customFieldsData = [],
  skipCreditDeduction = false,
  topicSmartSubjectLine = false,
  topicSmartSubjectStyle = 'neutral',
  topicSmartSubjectInstructions = '',
  frameworkPrompt = null,
  topicEmailPref = 'auto',
  customInstructions = null,
  selectedVariantPacing = null,
  textSignature = ''
) => {
  // Per-template model/language config with global fallback
  const model = topicModel || aiSettings.model || 'gpt-4.1-mini';
  const maxTokens = topicMaxTokens || parseInt(aiSettings.max_tokens || aiSettings.maxTokens) || 500;
  const language = topicLanguage || aiSettings.language || 'auto';
  
  // Bestimme den Betreff basierend auf dem Topic und dem Kontakt
  console.log('📧 [BULK-API] Determining subject line for topic:', selectedTopic);
  console.log('📧 [BULK-API] Contact has tags:', !!contact.tags);
  
  // 🔥 FIXED: Hole eine kontaktspezifische Betreffzeile MIT ID für Tracking
  let subjectLineId = null;
  let allStaticSubjectLines = []; // For pool-based random selection with smart subject

  // Follow-up reply-to-previous: detect BEFORE running the subject pool logic.
  // Use findTopicInUserTopics so we match by both topic name and type mapping
  // (e.g. "First Follow-up" → follow_up_1), identical to BulkModeEmail / NormalMode.
  const matchedTopicForMode = findTopicInUserTopics(userTopics, selectedTopic);
  const replySubjectForThisContact = matchedTopicForMode && matchedTopicForMode.subjectMode === 'reply_to_previous'
    ? resolveFollowUpSubject(matchedTopicForMode, contact)
    : null;
  const useReplySubject = !!replySubjectForThisContact;
  if (useReplySubject) {
    subjectLineId = SUBJECT_REPLY_LINE_ID;
    console.log('📧 [BULK-API] ✅ Reply-to-previous subject resolved:', replySubjectForThisContact);
  }
  const subjectLine = await (async () => {
    // Short-circuit reply-to-previous: uses contact.lead_last_email.subject
    if (useReplySubject) return replySubjectForThisContact;

    // 🔥 NEUE LOGIK: Lade die AI Tag Settings direkt aus der Datenbank (wie in BulkModeEmail.js)
    if (contact.tags && typeof contact.tags === 'object') {
      const tagsArray = Object.entries(contact.tags).map(([tagId, tagData]) => ({
        id: tagId,
        ...tagData
      }));
      
      // Sortiere nach appliedAt Datum (neueste zuerst)
      tagsArray.sort((a, b) => {
        const dateA = new Date(a.appliedAt || 0);
        const dateB = new Date(b.appliedAt || 0);
        return dateB - dateA;
      });
      
      if (tagsArray.length > 0) {
        const newestTag = tagsArray[0];
        
        // 🔥 WICHTIG: Lade die AI Tag-Einstellungen direkt aus der Datenbank
        if (newestTag.id) {
          try {
            // Lade die AI Tag-Einstellungen
            const aiTagRef = ref(database, `/World/${userId}/settings/aiTags/${newestTag.id}`);
            const aiTagSnapshot = await new Promise((resolve) => {
              onValue(aiTagRef, resolve, { onlyOnce: true });
            });
            
            if (aiTagSnapshot.exists()) {
              const aiTagData = aiTagSnapshot.val();
              console.log('📧 [BULK-API] ✅ Loaded AI Tag settings from database:', {
                selectedTopic: aiTagData.selectedTopic,
                tag: aiTagData.tag,
                name: aiTagData.name
              });
              
              if (aiTagData.selectedTopic === selectedTopic) {
                // Finde das matching Topic in userTopics für die Subject Line
                const matchingTopicEntry = Object.entries(userTopics).find(([topicId, topicData]) => {
                  return topicData.topic === aiTagData.selectedTopic || topicData.type === aiTagData.selectedTopic;
                });
                
                if (matchingTopicEntry) {
                  const [topicId, matchingTopic] = matchingTopicEntry;
                  let subject = "";
                  
                  // Capture all static subject lines for pool-based smart subject selection
                  if (Array.isArray(matchingTopic.subjectLines)) allStaticSubjectLines = matchingTopic.subjectLines;

                  // 🔥 FIXED: Zufällige Auswahl MIT ID-Tracking
                  if (matchingTopic.subjectLines && Array.isArray(matchingTopic.subjectLines) && matchingTopic.subjectLines.length > 0) {
                    console.log('🎲 [HELPER-API-ROTATION] Multiple subject lines found in AI Tag, selecting via rotation:', matchingTopic.subjectLines);
                    // Rotation / shuffle-bag keyed per template so all subject lines cycle evenly.
                    const randomSubjectLine = selectRandomWithRotation(matchingTopic.subjectLines, topicId);
                    console.log('🎲 [HELPER-API-ROTATION] Selected subject line:', randomSubjectLine, '(rotationKey:', topicId, ')');
                    subject = randomSubjectLine ? randomSubjectLine.text : "";
                    subjectLineId = randomSubjectLine && randomSubjectLine.id ? randomSubjectLine.id : `legacy_${topicId}`;
                  } else if (matchingTopic.subjectLine) {
                    console.log('🎲 [HELPER-API-RANDOM] Single subject line found in AI Tag:', matchingTopic.subjectLine);
                    subject = matchingTopic.subjectLine;
                    subjectLineId = `legacy_${topicId}`;
                  }
                  
                  if (subject) {
                    const rName = recipientInfo ? recipientInfo.name : (getFirstDmName(contact) || '');
                    const rRole = recipientInfo ? recipientInfo.role : '';
                    subject = subject.replace(/{{business_name}}/g, contact.business_name || '');
                    subject = subject.replace(/{{name}}/g, getFirstDmName(contact) || '');
                    subject = subject.replace(/{{company}}/g, '');
                    subject = subject.replace(/{{\s*recipient_name\s*}}/g, rName);
                    subject = subject.replace(/{{\s*recipient_role\s*}}/g, rRole);
                    console.log('📧 [BULK-API] ✅ Using AI Tag subject line from database settings:', subject, 'ID:', subjectLineId);
                    return subject;
                  }
                }
              }
            } else {
              console.log('📧 [BULK-API] ⚠️ AI Tag settings not found for ID:', newestTag.id);
            }
          } catch (error) {
            console.error('📧 [BULK-API] Error loading AI Tag settings:', error);
          }
        }
      }
    }
    
    console.log('📧 [BULK-API] Using standard logic for subject');
    
    // Fallback: name-first search, then type-based fallback
    const topicType = topicTypeMapping[selectedTopic];
    const matchingTopicEntry = Object.entries(userTopics).find(([topicId, topic]) => topic && topic.topic === selectedTopic)
      || (topicType ? Object.entries(userTopics).find(([topicId, topic]) => topic && topic.type === topicType) : null);
    if (matchingTopicEntry) {
      const [topicId, matchingTopic] = matchingTopicEntry;
      let subject = "";
      
      // Capture all static subject lines for pool-based smart subject selection
      if (Array.isArray(matchingTopic.subjectLines)) allStaticSubjectLines = matchingTopic.subjectLines;

      if (matchingTopic.subjectLines && Array.isArray(matchingTopic.subjectLines) && matchingTopic.subjectLines.length > 0) {
        console.log('🎲 [HELPER-API-FALLBACK-ROTATION] Multiple subject lines found in fallback, selecting via rotation:', matchingTopic.subjectLines);
        // Rotation / shuffle-bag keyed per template so all subject lines cycle evenly.
        const randomSubjectLine = selectRandomWithRotation(matchingTopic.subjectLines, topicId);
        console.log('🎲 [HELPER-API-FALLBACK-ROTATION] Selected subject line:', randomSubjectLine, '(rotationKey:', topicId, ')');
        subject = randomSubjectLine ? randomSubjectLine.text : "";
        subjectLineId = randomSubjectLine && randomSubjectLine.id ? randomSubjectLine.id : `legacy_${topicId}`;
      } else if (matchingTopic.subjectLine) {
        console.log('🎲 [HELPER-API-FALLBACK-RANDOM] Single subject line found in fallback:', matchingTopic.subjectLine);
        subject = matchingTopic.subjectLine;
        subjectLineId = `legacy_${topicId}`;
      }
      
      if (subject) {
        const rName = recipientInfo ? recipientInfo.name : (getFirstDmName(contact) || '');
        const rRole = recipientInfo ? recipientInfo.role : '';
        subject = subject.replace(/{{business_name}}/g, contact.business_name || '');
        subject = subject.replace(/{{name}}/g, getFirstDmName(contact) || '');
        subject = subject.replace(/{{company}}/g, '');
        subject = subject.replace(/{{\s*recipient_name\s*}}/g, rName);
        subject = subject.replace(/{{\s*recipient_role\s*}}/g, rRole);
        return subject;
      }
    }
    
    // Standard-Betreffzeilen als Fallback
    const defaultSubjects = {
      "Introduce Yourself": `Boost für ${contact.business_name || ''}`,
      "Follow Up": `Nachfrage - Partnerschaft mit ${contact.business_name || ''}`,
      "Second Follow Up": `Kurzes Update für ${contact.business_name || ''}`,
      "Third Follow Up": `Finale Nachfrage für ${contact.business_name || ''}`
    };
    
    return defaultSubjects[selectedTopic] || `Partnerschaftsanfrage für ${contact.business_name || ''}`;
  })();
  
  console.log('📧 [BULK-API] ✅ Subject line selected with tracking ID:', { subjectLine, subjectLineId });
  
  // Erstelle das Request-Objekt mit allen notwendigen Daten
  const requestData = {
    topic: selectedTopic,
    context: prompt,
    businessName: contact.business_name || '',
    website: contact.lead_website || '',
    senderName: userSettings.sender_name || userSettings.user_name || '',
    senderEmail: userSettings.user_email || '',
    textSignature: textSignature || accountTextSignature(userSettings),
    firstName: getFirstDmName(contact),
    recipientName: recipientInfo ? recipientInfo.name : (getFirstDmName(contact) || ''),
    recipientRole: recipientInfo ? recipientInfo.role : '',
    topicSpecificNote: resolvedTopicSpecificNote || getTopicSpecificNoteForBulk(selectedTopic, contact, userTopics, topicTypeMapping, getDefaultTopicNote, { isDecisionMakerRecipient: !!(recipientInfo?.name) }),
    model,
    maxTokens,
    language,
    generalInfo: topicGeneralInfo || '',
    aiAdherence: topicAiAdherence || 'exact',
    formattingStyle: topicFormattingStyle || 'as-written',
    writingLevel: topicWritingLevel || 'middle_school',
    ...(topicAiTemperature != null ? { aiTemperature: topicAiTemperature } : {}),
    hyperIntelligence: topicHyperIntelligence || false,
    personalizationGuide: topicPersonalizationGuide || '',
    referenceFile: topicReferenceFile || null,
    ultraMode: topicUltraMode || false,
    smartGreeting: topicSmartGreeting || false,
    greetingCasualPrefixes: topicGreetingCasualPrefixes || ['Hey', 'Hi', 'Hello'],
    greetingFormalPrefixes: topicGreetingFormalPrefixes || ['Dear'],
    greetingTone: greetingToneFromVariant(selectedVariantPacing),
    smartSubjectLine: useReplySubject ? false : (topicSmartSubjectLine || false),
    smartSubjectStyle: topicSmartSubjectStyle || 'neutral',
    smartSubjectInstructions: [aiSettings.subjectLineGuidelines, topicSmartSubjectInstructions].filter(Boolean).join('\n') || '',
    existingSubjects: [],
    leadPacing: getEffectivePacing(contact?.lead_pacing || null, !!(recipientInfo?.name), topicEmailPref),
    userId,
    templateId: templateId || null,
    contactId: contact?.id || null,
    customFieldsData: customFieldsData || [],
    ...(frameworkPrompt ? { frameworkPrompt } : {}),
    ...(customInstructions ? { customInstructions } : {}),
    ...(hasInstructionBlocks(subjectLine) ? { subjectLineTemplate: subjectLine } : {}),
    // Precompute 4 brand-voice subject alternatives during body-gen so the
    // ⚡ button opens instantly on the first click for each bulk contact.
    // Skipped on reply-to-previous (subject inherited from the thread).
    precomputeSubjectAlternatives: !useReplySubject,
    // Static subject variants (no {instruction}) — passed as BRAND-VOICE
    // reference so the suite matches campaign tone.
    staticSubjectLinesForVoice: (allStaticSubjectLines || [])
      .map(sl => (sl && typeof sl === 'object' ? sl.text : sl))
      .filter(t => typeof t === 'string' && t.trim() && !hasInstructionBlocks(t)),
  };

  // Follow-up context: give the writer the email we last sent this contact so it
  // can match style/content and not repeat the same pitch verbatim.
  if (isFollowUpTopicType(topicTypeMapping?.[selectedTopic])) {
    const previousEmail = await fetchPreviousSentEmail(
      userId,
      contact,
      [recipientInfo?.email, contact?.lead_email, contact?.email].filter(Boolean)
    );
    if (previousEmail) {
      requestData.previousEmail = previousEmail;
    }
  }

  try {
    if (userId && !skipCreditDeduction) {
      const hasRefFile = !!(topicReferenceFile?.claudeFileId);
      const emailCreditCost = emailGenerationCost({
        hyperIntelligence: topicHyperIntelligence,
        hasReferenceFile: hasRefFile,
      });
      const actionLabel = topicHyperIntelligence ? (hasRefFile ? 'hyperintelligence_pdf_email' : 'hyperintelligence_email') : 'email_generation';
      try {
        await chargeCredits({
          action: actionLabel,
          amount: emailCreditCost,
          worldId: userId,
          metadata: { contactId: contact.id, topic: selectedTopic, hyperIntelligence: topicHyperIntelligence, hasReferenceFile: hasRefFile },
        });
      } catch (creditError) {
        if (creditError.code === 'insufficient_credits') {
          throw new Error(`Not enough credits for email generation (${emailCreditCost} credits required). Please add more credits to continue.`);
        }
        // Rate limits and transient errors were retried inside chargeCredits.
        // Generating anyway would hand out a Hyper-Intelligence mail (up to 34
        // credits) for free, so this aborts instead.
        throw new Error(
          `Could not reserve ${emailCreditCost} credits for the email. Nothing was generated — please retry.`
        );
      }
    }

    const MAX_RETRIES = 2;
    const BASE_DELAY = 3000;
    let lastError;
    let data;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        });

        if (response.ok) {
          data = await response.json();
          break;
        }

        let serverDetails = '';
        try {
          const errBody = await response.json();
          serverDetails = errBody.details || errBody.error || '';
        } catch (_) { /* response may not be JSON */ }

        const isRetryable = response.status === 429 || response.status >= 500;
        if (!isRetryable || attempt >= MAX_RETRIES) {
          throw new Error(`API-Fehler: ${response.status}${serverDetails ? ` (${serverDetails})` : ''}`);
        }

        const delay = BASE_DELAY * Math.pow(2, attempt) + Math.random() * 1000;
        console.log(`🔄 [BULK-API] Attempt ${attempt + 1} failed (${response.status}${serverDetails ? ': ' + serverDetails : ''}), retrying in ${Math.round(delay)}ms...`);
        await new Promise(r => setTimeout(r, delay));
      } catch (fetchError) {
        lastError = fetchError;
        const msg = (fetchError.message || '').toLowerCase();
        const isNetworkError = msg.includes('failed to fetch') || msg.includes('network') || msg.includes('timeout');
        if (!isNetworkError || attempt >= MAX_RETRIES) throw fetchError;

        const delay = BASE_DELAY * Math.pow(2, attempt) + Math.random() * 1000;
        console.log(`🔄 [BULK-API] Network error on attempt ${attempt + 1} (${fetchError.message}), retrying in ${Math.round(delay)}ms...`);
        await new Promise(r => setTimeout(r, delay));
      }
    }

    if (!data) throw lastError || new Error('API call failed after retries');
    
    if (data.emailContent) {
      let finalSubject = data.subjectLine || subjectLine;
      let finalSubjectLineId = subjectLineId;

      // Reply-to-previous always wins — keep the subject we pre-computed.
      if (useReplySubject) {
        finalSubject = subjectLine;
        finalSubjectLineId = SUBJECT_REPLY_LINE_ID;
      } else if (data.smartSubjectLine) {
        const validStaticLines = allStaticSubjectLines.filter(sl => {
          const text = typeof sl === 'object' ? sl.text : sl;
          return text && text.trim();
        });

        if (validStaticLines.length > 0) {
          const rName = recipientInfo ? recipientInfo.name : (getFirstDmName(contact) || '');
          const rRole = recipientInfo ? recipientInfo.role : '';
          const pool = [
            { text: data.smartSubjectLine, id: 'smart_subject' },
            ...validStaticLines
              .filter(sl => {
                const raw = typeof sl === 'object' ? sl.text : sl;
                return !hasInstructionBlocks(raw);
              })
              .map(sl => {
                let text = typeof sl === 'object' ? sl.text : sl;
                text = text.replace(/{{business_name}}/g, contact.business_name || '')
                          .replace(/{{name}}/g, getFirstDmName(contact) || '')
                          .replace(/{{company}}/g, '')
                          .replace(/{{\s*recipient_name\s*}}/g, rName)
                          .replace(/{{\s*recipient_role\s*}}/g, rRole);
                return { text, id: typeof sl === 'object' ? sl.id : null };
              })
          ];
          if (data.resolvedSubjectLine) {
            pool.push({ text: data.resolvedSubjectLine, id: 'resolved_subject' });
          }
          const pick = pool[Math.floor(Math.random() * pool.length)];
          console.log(`🎲 [SUBJECT-POOL] Pool size: ${pool.length}, picked:`, pick);
          finalSubject = pick.text;
          finalSubjectLineId = pick.id;
        } else {
          finalSubject = data.smartSubjectLine;
          finalSubjectLineId = 'smart_subject';
        }
      } else if (data.resolvedSubjectLine) {
        console.log('📝 [SUBJECT-INSTR] Using resolved subject from backend:', data.resolvedSubjectLine);
        finalSubject = data.resolvedSubjectLine;
      }

      if (hasInstructionBlocks(finalSubject)) {
        finalSubject = stripInstructionBlocks(finalSubject);
      }

      // Prefer resolved-instruction alternatives; fall back to the generic
      // brand-voice suite alternatives. Frontend caches these so the ⚡
      // button opens instantly for the current bulk contact.
      const precomputedAlts = (data.resolvedSubjectAlternatives && data.resolvedSubjectAlternatives.length > 0)
        ? data.resolvedSubjectAlternatives
        : (Array.isArray(data.subjectAlternatives) ? data.subjectAlternatives : []);

      return {
        subject: finalSubject,
        body: data.emailContent,
        subjectLineId: finalSubjectLineId,
        subjectAlternatives: precomputedAlts,
      };
    } else {
      throw new Error('Unerwartetes API-Antwortformat');
    }
  } catch (error) {
    console.error('❌ Fehler beim API-Aufruf:', error);
    throw new Error(`API-Aufruf fehlgeschlagen für ${contact.business_name || 'Kontakt'}: ${error.message}`);
  }
};

// Kontaktstatus aktualisieren
export const updateContactStatus = async (userId, leadId, newStatus, onEmailResult, recipientEmail, contactData) => {
  // Überprüfe, ob alle erforderlichen Parameter vorhanden sind
  if (!userId || !leadId || !newStatus) {
    console.warn("updateContactStatus: Fehlende Parameter", { userId, leadId, newStatus });
    return false;
  }
  
  try {
    // Erstelle einen Timestamp für konsistente Verwendung
    const timestamp = Date.now();
    
    // Debug-Nachricht für Statusänderung
    console.log(`Führe optimistisches Update für Lead ${leadId} auf "${newStatus}" durch`);
    
    // Optimistisches UI-Update
    if (onEmailResult) {
      // Erstelle das Update-Objekt mit allen notwendigen Informationen
      const statusUpdateInfo = {
        id: leadId,
        field: 'lead_contacted',
        value: newStatus,
        timestamp: timestamp,
        _optimistic_update: true,
        _prevent_refresh: true,
        _critical_update: true
      };
      
      // Sende das Status-Update zur übergeordneten Komponente
      onEmailResult('status-update', statusUpdateInfo);
      
      // Zusätzlich für die lokale UI aktualisieren
      const localUpdate = {
        id: leadId,
        field: 'lead_status',
        value: newStatus,
        timestamp: timestamp,
        _optimistic_update: true
      };
      onEmailResult('status-update', localUpdate);
    }
    
    // Bereite die Aktualisierungsdaten für Firebase vor
    const updateData = {
      lead_contacted: newStatus,
      lead_status: newStatus,
      last_contact_date: new Date().toISOString(),
      updated_at: timestamp,
      _optimistic_update: true,
      _client_update_timestamp: timestamp
    };
    
    if (newStatus !== "Not Contacted") {
      updateData.lead_last_email = {
        timestamp: timestamp,
        status: newStatus,
        ...(recipientEmail ? { recipientEmail } : {}),
        ...(contactData?.leadSignals ? { leadSignalsSnapshot: minimalSignalsSnapshot(contactData.leadSignals) } : {}),
      };
    }
    
    // Datenbank aktualisieren – jetzt Firestore statt Realtime DB
    const contactDocRef = doc(firestore, `World/${userId}/db/${leadId}`);
    await updateDoc(contactDocRef, updateData);
    if (contactData) {
      logStatusChange(firestore, userId, leadId, contactData.lead_contacted || contactData.lead_status, newStatus, 'after sending an email');
    }
    
    console.log(`Status für Lead ${leadId} erfolgreich aktualisiert auf "${newStatus}"`);
    return true;
  } catch (error) {
    console.error("Fehler beim Aktualisieren des Kontaktstatus:", error);
    return false;
  }
};

// Letzten Absender in der Datenbank speichern
export const saveLastSenderToDatabase = async (senderEmail, userId) => {
  try {
    if (!userId || !senderEmail) return false;
    
    // Speichere den letzten verwendeten Absender in den Benutzereinstellungen
    const lastSenderRef = ref(database, `/World/${userId}/settings/lastUsedSender`);
    await set(lastSenderRef, {
      email: senderEmail,
      timestamp: Date.now(),
      updatedAt: new Date().toISOString()
    });
    
    console.log(`Letzter verwendeter Absender (${senderEmail}) in der Datenbank gespeichert`);
    return true;
  } catch (error) {
    console.error('Fehler beim Speichern des letzten Absenders:', error);
    return false;
  }
}; 